import { useState, useEffect, useCallback } from 'react'
import { NotificationType } from '../../../api-client/enums'
import FavoriteToggle from '../../../components/favorite-toggle'
import LocationPin from '../../../components/svg/location-pin'
import Shipping from '../../../components/svg/shipping'
import Speedometer from '../../../components/svg/speedometer'
import useFavorites from '../../../hooks/use-favorites'
import { PriceRibbon } from '../../../components/ribbon'
import useLocation from '../../../hooks/use-location'
import useNotifications from '../../../hooks/use-notifications'
import useVehiclePageData from '../use-vehicle-page-data'
import getDistance from '../../../utils/get-distance'
import { UNITS } from '../../../constants'
import { useAuth } from '../../../contexts/auth'

export default function LotInfo({ sellerConciergeLocation, shipmentRate }) {
  const { vehicleData, auctionInfo } = useVehiclePageData()
  const { favorites, toggleFavorite, favoritesProcessing } = useFavorites()
  const { markNotificationAsRead } = useNotifications(NotificationType.Vehicle)
  const [userLocation, setUserLocation, userLocationIsResolving] = useLocation()
  const [distance, setDistance] = useState()
  const [{ isAuthenticated }] = useAuth()
  const distanceUnits = UNITS.MILES

  useEffect(() => {
    if (!!userLocationIsResolving) {
      console.log('userLocation is still resolving')
      return
    }
    if (!userLocation) {
      console.log('userLocation is still not ready')
      return
    }
    if (userLocation.lat == null && userLocation.lng == null) {
      console.log('userLocation.lat and userLocation.lng not found')
      return
    }
    if (!sellerConciergeLocation) {
      console.log('vehicleLocation is still resolving')
      return
    }
    if (sellerConciergeLocation.lat == null && sellerConciergeLocation.lng == null) {
      console.log('vehicleLocation.lat and vehicleLocation.lng not found')
      return
    }

    console.log('getting distance from user to seller vehicle concierge')

    let distanceFromYouToSellerVehicleConcierge = getDistance(
      { lat: sellerConciergeLocation.lat, lng: sellerConciergeLocation.lng },
      { lat: userLocation.lat, lng: userLocation.lng },
      distanceUnits,
    )

    let distanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer =
      distanceFromYouToSellerVehicleConcierge + sellerConciergeLocation.distanceFromYourLocationKey

    let roundedDistanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer = Math.round(
      distanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer,
    )

    console.log(
      'distance from user to seller vehicle concierge:',
      distanceFromYouToSellerVehicleConcierge,
      'distance from seller to seller concierge:',
      sellerConciergeLocation.distanceFromYourLocationKey,
      'roundedDistanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer:',
      roundedDistanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer,
    )

    setDistance(roundedDistanceThatVehicleHasToTravelToBeDeliveredFromSellerToBuyer)
  }, [userLocation, userLocationIsResolving, sellerConciergeLocation])

  const handleFavoriteClick = useCallback(() => {
    toggleFavorite(vehicleData?.id, favorites[vehicleData.id]?.isFavorite)
    markNotificationAsRead(vehicleData?.id)
  }, [vehicleData, toggleFavorite, favorites, markNotificationAsRead])

  return (
    <div className='lotInfo'>
      <PriceRibbon
        className='priceRibbon'
        amount={auctionInfo?.topBidAmount || vehicleData.desiredBuyNowPrice || 0}
      />
      <div className='vehicle'>
        <span className='name'>
          <strong>
            {vehicleData.year} {vehicleData.make} <br className='nameBreak' />{' '}
            {vehicleData.model.replace('-', '\u2011')}{' '}
            {/* regular hypens are replaced with non-breaking hyphens to avoid line breaks in vehicle models like "F-150"*/}
          </strong>
        </span>
        <br />
        <span className='trim'>{vehicleData.trim}</span>
      </div>

      <div className='flex alignStart justifyBetween favoriteBtnContainer'>
        <div className='factsWrapper flex alignStart flexDirectionColumn'>
          <div className='distance'>
            {!!sellerConciergeLocation && (
              <span>
                <LocationPin fill='var(--primary-text-color)' width='17' height='17' />
                <span>{distance ? `${distance} ${distanceUnits} away` : 'N/A'} </span>
              </span>
            )}
            {((!!shipmentRate && shipmentRate.isSucceeded) || !isAuthenticated) && (
              <span>
                <Shipping fill='var(--primary-text-color)' width='15' height='15' />
                <span>{isAuthenticated ? `$${shipmentRate?.rate} shipping` : 'Sign In'}</span>
              </span>
            )}
            <span>
              <Speedometer fill='var(--primary-text-color)' width='15' height='15' />
              <span>
                {vehicleData.mileage.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}{' '}
                mi
              </span>
            </span>
          </div>
        </div>

        <FavoriteToggle
          size='sm'
          isFavorite={favorites[vehicleData.id]?.isFavorite}
          onClick={handleFavoriteClick}
          isFavoriteProcessing={!!favoritesProcessing[vehicleData.id]?.isProcessing}
        />
      </div>

      <style jsx>{`
        .lotInfo {
          position: relative;

          :global(.priceRibbon) {
            right: -2rem;
          }

          :global(.favoriteToggle) {
            width: 2.3rem;
            height: 2.1rem;
          }
        }

        .vehicle {
          min-height: 4.5rem;
          width: 60%;

          .name {
            font-size: 2rem;
            font-weight: 700;
            word-break: break-word;
          }

          .trim {
            color: var(--secondary-text-color);
            font-size: 1.4rem;
          }
        }

        .favoriteBtnContainer {
          margin: 1.2rem 0 0.6rem;

          .distance {
            margin: 0 1.2rem 0 0;
            font-size: 1.4rem;
            font-weight: bold;
            color: var(--primary-color);
            margin-bottom: 0.4rem;

            :global(svg),
            span {
              margin: 0 0.6rem 0 0;
              vertical-align: middle;
            }
          }
        }

        .nameBreak {
          display: none;
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .nameBreak {
            display: block;
          }

          .lotInfo {
            :global(.priceRibbon) {
              font-size: 2rem;
            }
          }

          .vehicle {
            width: 55%;
          }
        }

        @media (max-width: 1024px) {
          .lotInfo {
            margin: 0.8rem 0 0;
            overflow: hidden;
          }

          .factsWrapper {
            flex-direction: row;

            .distance {
              margin-bottom: 0;
            }
          }

          .vehicle {
            max-width: 21rem;
            width: 100%;

            .name {
              font-size: 1.7rem;
            }

            .trim {
              color: var(--secondary-text-color);
              font-size: 1.2rem;
            }
          }
        }
      `}</style>
    </div>
  )
}
